import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Container, Loader2, Building2 } from "lucide-react";

export default function Signup() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ company_name: "", name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await signup({ ...form, email: form.email.trim().toLowerCase() });
    setLoading(false);
    if (res.ok) {
      toast.success("Company created — your 14-day free trial has started");
      navigate("/");
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 relative overflow-hidden px-4 py-10">
      <div className="absolute inset-0 opacity-[0.15]" style={{ backgroundImage: "linear-gradient(#1e293b 1px, transparent 1px), linear-gradient(90deg, #1e293b 1px, transparent 1px)", backgroundSize: "44px 44px" }} />
      <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-orange-600/20 blur-3xl" />

      <div className="relative w-full max-w-md bg-slate-900/90 border border-slate-700 rounded-lg p-8 shadow-2xl backdrop-blur-md" data-testid="signup-card">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-11 w-11 rounded-md bg-orange-600 flex items-center justify-center shadow-lg shadow-orange-900/40">
            <Container className="h-6 w-6 text-white" />
          </div>
          <div>
            <div className="font-heading font-extrabold tracking-wide text-2xl uppercase leading-none">
              Onlogist<span className="text-orange-500"> Pro</span>
            </div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500 mt-1">Start your 14-day free trial</div>
          </div>
        </div>

        <h1 className="text-slate-100 text-lg font-semibold mb-1">Create your company</h1>
        <p className="text-sm text-slate-400 mb-6">You'll be the Super Administrator of a private, isolated workspace.</p>

        <form onSubmit={submit} className="space-y-4">
          <Field label="Company name" testid="signup-company" value={form.company_name} onChange={set("company_name")} placeholder="Acme Manufacturing Ltd" icon={<Building2 className="h-4 w-4 text-slate-500" />} />
          <Field label="Your full name" testid="signup-name" value={form.name} onChange={set("name")} placeholder="Jane Doe" />
          <Field label="Work email" testid="signup-email" type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" />
          <Field label="Password" testid="signup-password" type="password" value={form.password} onChange={set("password")} placeholder="At least 6 characters" />
          <button type="submit" disabled={loading} data-testid="signup-submit-button"
            className="w-full flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-500 disabled:opacity-60 text-white font-semibold px-4 py-2.5 rounded-md shadow-md transition-all active:scale-[0.98]">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Building2 className="h-4 w-4" />}
            {loading ? "Creating..." : "Start free trial"}
          </button>
        </form>

        <div className="mt-5 pt-5 border-t border-slate-800 text-center text-sm text-slate-400">
          Already have an account?{" "}
          <a href="/login" data-testid="signup-login-link" className="text-orange-400 hover:text-orange-300 font-semibold transition-colors">Sign in</a>
        </div>
      </div>
    </div>
  );
}

function Field({ label, testid, value, onChange, type = "text", placeholder }) {
  return (
    <div>
      <label className="font-mono text-xs uppercase tracking-wider text-slate-400">{label}</label>
      <input
        type={type} required value={value} onChange={onChange} data-testid={testid} placeholder={placeholder}
        className="mt-1.5 w-full rounded-md bg-slate-950 border border-slate-700 px-3 py-2.5 text-sm text-slate-100 outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors"
      />
    </div>
  );
}
